import { useEffect, useState } from "react";
import { useLang } from "@/lib/lang";
import UploadDropzone from "@/components/UploadDropzone";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { toast } from "sonner";

const MAX_SELECT = 5;

export default function ChatContextDialog({
  open,
  onOpenChange,
  documents,
  selectedIds,
  onSave,
  onDocumentUploaded,
}) {
  const { t } = useLang();
  const [draftIds, setDraftIds] = useState(selectedIds);

  useEffect(() => {
    if (open) {
      setDraftIds(selectedIds);
    }
  }, [open, selectedIds]);

  const toggleDoc = (docId) => {
    setDraftIds((ids) => {
      if (ids.includes(docId)) {
        return ids.filter((id) => id !== docId);
      }
      if (ids.length >= MAX_SELECT) {
        toast.error(t("task_need_docs").replace("{n}", String(MAX_SELECT)));
        return ids;
      }
      return [...ids, docId];
    });
  };

  const handleUploaded = (meta) => {
    onDocumentUploaded?.(meta);
    setDraftIds((ids) => {
      if (ids.includes(meta.id)) return ids;
      if (ids.length >= MAX_SELECT) {
        toast.error(t("task_need_docs").replace("{n}", String(MAX_SELECT)));
        return ids;
      }
      return [...ids, meta.id];
    });
  };

  const handleSave = () => {
    if (draftIds.length === 0) {
      toast.error(t("chat_context_select_required"));
      return;
    }
    onSave?.(draftIds);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none sm:max-w-md grid-border max-h-[85vh] flex flex-col" data-testid="chat-context-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-lg">{t("chat_context")}</DialogTitle>
          <p className="text-xs text-slate-500">{t("chat_context_select")}</p>
        </DialogHeader>

        <div className="space-y-3 overflow-y-auto flex-1 py-2">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">{t("chat_context_upload")}</p>
            <UploadDropzone compact onUploaded={handleUploaded} />
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
              {t("chat_context_custom")}
              {draftIds.length > 0 && (
                <span className="normal-case tracking-normal ms-2 text-slate-400">
                  ({draftIds.length} / {MAX_SELECT})
                </span>
              )}
            </p>
            <div className="grid-border max-h-48 overflow-y-auto">
              {documents.length === 0 ? (
                <p className="px-4 py-3 text-xs text-slate-500">{t("chat_empty_hint")}</p>
              ) : (
                documents.map((doc) => {
                  const checked = draftIds.includes(doc.id);
                  return (
                    <button
                      key={doc.id}
                      type="button"
                      data-testid={`context-doc-${doc.id}`}
                      onClick={() => toggleDoc(doc.id)}
                      className={[
                        "w-full flex items-center gap-3 px-4 py-2.5 text-start grid-border-b last:border-b-0 transition-colors",
                        checked ? "bg-slate-50" : "hover:bg-slate-50/50",
                      ].join(" ")}
                    >
                      <div className={[
                        "w-4 h-4 flex items-center justify-center flex-shrink-0 grid-border",
                        checked ? "bg-slate-900 text-white" : "bg-white",
                      ].join(" ")}>
                        {checked && <Check size={10} />}
                      </div>
                      <span className="text-xs truncate">{doc.filename}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-none"
          >
            {t("cancel")}
          </Button>
          <Button
            type="button"
            data-testid="context-save-btn"
            onClick={handleSave}
            className="rounded-none bg-slate-900 hover:bg-slate-800 text-white"
          >
            {t("chat_context_save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
